import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors,
  FormControl,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from "app/main/services/Api/api.service";
import { MediaService } from "app/main/services/Media/media.service";
import { HttpErrorResponse } from "@angular/common/http";
import { ToastService } from "app/main/services/Toast/toast.service";

interface EventSchedule {
  startTime: string;
  endTime: string;
  eventTicketsTypes: EventTicketType[];
}

interface EventTicketType {
  name: string;
  note: string;
  price: number;
  availableTickets: number;
  maximumAllowedPurchasedTicketsPerCustomer: number;
}

@Component({
  selector: "app-events-action",
  templateUrl: "./events-action.component.html",
  styleUrls: ["./events-action.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class EventsActionComponent implements OnInit, OnDestroy {
  public contentHeader: object;
  public eventForm: FormGroup;
  public previewUrl1: string | ArrayBuffer | null = null;
  public previewUrl2: string | ArrayBuffer | null = null;
  public selectedCoverPhoto: File | null = null;
  public selectedHeadPhoto: File | null = null;
  public isLoading = false;
  public editMode = false;
  public successMessage = "";
  public errorMessage = "";
  public eventId: string | null = null;
  public uploadedCoverPath: string | null = null;
  public uploadedHeadPath: string | null = null;
  public previousUploadedCoverPath: string | null = null;
  public previousUploadedHeadPath: string | null = null;
  public isSaved = false;
  public isActive = true;

  public eventType: any;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private api: ApiService,
    private mediaService: MediaService,
    public toastService: ToastService,
    private url: ActivatedRoute
  ) {
    this.initializeForm();
    // this.setupDateValidation();
  }

  ngOnInit(): void {
    this.setupActiveMenuItems();
    this.initializeContentHeader();
    this.setEventCategory();
  }

  private initializeForm(): void {
    this.eventForm = this.fb.group({
      nameAr: ["", [Validators.required, this.arabicTextValidator]],
      nameEn: ["", [Validators.required, this.englishTextValidator]],
      description: ["", Validators.required],
      policy: ["", Validators.required],
      country: ["", Validators.required],
      city: ["", Validators.required],
      latitude: ["", Validators.required],
      longitude: ["", Validators.required],
      address: ["", Validators.required],
      categoryId: ["", Validators.required],
      eventSchedules: this.fb.array([this.createEventSchedule()]),
      isActive: [true],
    });
  }

  createEventSchedule(): FormGroup {
    let validators = [
      this.dateRangeValidator(),
      this.cinematicTicketTypeValidator(),
      this.startTimeAfterNowValidator(),
    ];
    const scheduleForm = this.fb.group(
      {
        id: [null],
        isCenematic: [false],
        startTime: ["", Validators.required],
        endTime: ["", Validators.required],
        hallRowSeatDistribution: this.fb.array([]),
        eventTicketsTypes: this.fb.array([this.createTicketType()]),
      },
      {
        validator: validators,
      }
    );
    scheduleForm.get("hallRowSeatDistribution")?.valueChanges.subscribe(() => {
      const index = this.eventSchedules.length;
      this.updateAvailableTickets(index - 1);
    });
    return scheduleForm;
  }
  getNumberOfTickets(i: number) {
    console.log(this.eventSchedules);
    const distribution =
      this.eventSchedules.at(i).get("hallRowSeatDistribution")?.value || [];
    const totalSeats = distribution.reduce(
      (sum: number, item: any) => sum + (item.seats || 0),
      0
    );

    return totalSeats;
  }
  updateAvailableTickets(scheduleIndex: number): void {
    const totalSeats = this.getNumberOfTickets(scheduleIndex);

    const ticketTypes = this.eventSchedules
      .at(scheduleIndex)
      .get("eventTicketsTypes") as FormArray;
    ticketTypes.controls.forEach((ticketType) => {
      ticketType
        .get("availableTickets")
        ?.setValue(totalSeats, { emitEvent: false });
    });
  }
  createTicketType(scheduleIndex: number = 0): FormGroup {
    return this.fb.group(
      {
        id: [null],
        name: [
          "",
          [
            Validators.required,
            // Pass the scheduleIndex to the validator
            this.uniqueTicketNameValidator(scheduleIndex),
          ],
        ],
        note: [""],
        price: [0, [Validators.required, Validators.min(0)]],
        availableTickets: [0, [Validators.required, Validators.min(0)]],
        maximumAllowedPurchasedTicketsPerCustomer: [
          0,
          [Validators.required, Validators.min(1)],
        ],
      },
      { validator: [this.maximumOfTicketRangeValidator()] }
    );
  }

  get eventSchedules(): FormArray {
    return this.eventForm.get("eventSchedules") as FormArray;
  }

  getTicketTypes(scheduleIndex: number): FormArray {
    return this.eventSchedules
      .at(scheduleIndex)
      .get("eventTicketsTypes") as FormArray;
  }

  addEventSchedule(): void {
    this.eventSchedules.push(this.createEventSchedule());
  }

  removeEventSchedule(index: number): void {
    this.eventSchedules.removeAt(index);
  }
  // Getter للـ HallRowSeatDistribution
  getHallRowSeatDistribution(scheduleIndex: number): FormArray {
    return this.eventSchedules
      .at(scheduleIndex)
      .get("hallRowSeatDistribution") as FormArray;
  }

  // إضافة رقم للـ HallRowSeatDistribution
  addRowNumber(scheduleIndex: number): void {
    (this.getHallRowSeatDistribution(scheduleIndex) as FormArray).push(
      this.createRowSeat()
    );
  }

  getHallRowSeatDistributionForEdit(scheduleIndex: number): FormArray {
    const scheduleGroup = this.eventSchedules.at(scheduleIndex) as FormGroup;

    let fa = scheduleGroup.get("hallRowSeatDistribution") as FormArray;

    // If for some reason it's missing/null, create it on the fly
    if (!fa) {
      fa = this.fb.array([]);
      scheduleGroup.setControl("hallRowSeatDistribution", fa);
    }

    return fa;
  }

  addEditRowNumber(scheduleIndex: number): void {
    this.getHallRowSeatDistributionForEdit(scheduleIndex).push(
      this.createRowSeat()
    );
  }

  createRowSeat(seatsNumber: number = 0): FormGroup {
    return this.fb.group({
      seats: [seatsNumber, Validators.required], // number of seats
    });
  }
  // حذف رقم من الـ HallRowSeatDistribution
  removeRowNumber(scheduleIndex: number, rowIndex: number): void {
    this.getHallRowSeatDistribution(scheduleIndex).removeAt(rowIndex);
  }
  addTicketType(scheduleIndex: number): void {
    this.getTicketTypes(scheduleIndex).push(
      this.createTicketType(scheduleIndex)
    );
  }

  removeTicketType(scheduleIndex: number, ticketIndex: number): void {
    this.getTicketTypes(scheduleIndex).removeAt(ticketIndex);
  }

  setEventCategory() {
    this.api.showAllEventCategory(1, 100).subscribe({
      next: (response) => {
        this.eventType = response.data.data.map((item, index) => ({
          eventType: item.nameAr || item.nameEn,
          id: item.id,
        }));
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
      },
    });
  }

  startTime: any;
  endTime: any;
  loadEventData(id: string) {
    if (id) {
      this.api.getEventById(id).subscribe({
        next: (response) => {
          this.eventForm.patchValue({
            nameAr: response.data.nameAr,
            nameEn: response.data.nameEn,
            isActive: response.data.isActive,
            description: response.data.description,
            policy: response.data.policy,
            country: response.data.country,
            city: response.data.city,
            categoryId: response.data.categoryId,
            address: response.data.address,
            longitude: response.data.longitude,
            latitude: response.data.latitude,
          });

          while (this.eventSchedules.length) {
            this.eventSchedules.removeAt(0);
          }

          if (
            response.data.eventSchedules &&
            response.data.eventSchedules.length
          ) {
            response.data.eventSchedules.forEach((schedule) => {
              let seatArray = null;
              if (schedule.hallRowSeatDistribution != null) {
                seatArray = this.fb.array(
                  schedule.hallRowSeatDistribution.map((seat) =>
                    this.createRowSeat(seat)
                  )
                );
              }
              this.startTime = schedule.startTime;
              this.endTime = schedule.endTime;
              const scheduleGroup = this.fb.group(
                {
                  id: schedule.id,
                  eventId: this.eventId,
                  startTime: [schedule.startTime, Validators.required],
                  endTime: [schedule.endTime, Validators.required],
                  hallRowSeatDistribution: seatArray,
                  isCenematic: [schedule.hallRowSeatDistribution != null && schedule.hallRowSeatDistribution.length > 0],
                  eventTicketsTypes: this.fb.array([]),
                },
                {
                  validator: [
                    this.dateRangeValidator(),
                    this.cinematicTicketTypeValidator(),
                  ],
                }
              );
              if (
                response.data.eventSchedules &&
                response.data.eventSchedules.length
              ) {
                // ✅ Always start with an empty FormArray
                const seatArray = this.fb.array([] as FormGroup[]);

                if (
                  schedule.hallRowSeatDistribution &&
                  schedule.hallRowSeatDistribution.length
                ) {
                  schedule.hallRowSeatDistribution.forEach((seat) => {
                    seatArray.push(this.createRowSeat(seat));
                  });
                }

                this.startTime = schedule.startTime;
                this.endTime = schedule.endTime;

                const scheduleGroup = this.fb.group({
                  id: schedule.id,
                  eventId: this.eventId,
                  startTime: [schedule.startTime, Validators.required],
                  endTime: [schedule.endTime, Validators.required],
                  // ✅ NEVER null. Always a FormArray.
                  hallRowSeatDistribution: seatArray,
                  isCenematic: [schedule.hallRowSeatDistribution != null && schedule.hallRowSeatDistribution.length > 0],
                  eventTicketsTypes: this.fb.array([]),
                  
                },
                {
                  validator: [
                    this.dateRangeValidator(),
                    this.cinematicTicketTypeValidator(),
                    // this.startTimeAfterNowValidator()
                  ],
                });

                if (
                  schedule.eventTicketsTypes &&
                  schedule.eventTicketsTypes.length
                ) {
                  schedule.eventTicketsTypes.forEach((ticket) => {
                    (scheduleGroup.get("eventTicketsTypes") as FormArray).push(
                      this.fb.group({
                        id: ticket.id,
                        eventId: this.eventId,
                        name: [ticket.name, Validators.required],
                        note: [ticket.note],
                        price: [
                          ticket.price,
                          [Validators.required, Validators.min(0)],
                        ],
                        availableTickets: [
                          ticket.availableTickets,
                          [Validators.required, Validators.min(0)],
                        ],
                        maximumAllowedPurchasedTicketsPerCustomer: [
                          ticket.maximumAllowedPurchasedTicketsPerCustomer,
                          [Validators.required, Validators.min(0)],
                        ],
                      })
                    );
                  });
                }

                this.eventSchedules.push(scheduleGroup);
              }
            });
          }
          this.isActive = response.data.isActive;
          this.previewUrl1 = response.data.headPhoto;
          this.previewUrl2 = response.data.coverPhoto;
          this.uploadedHeadPath = response.data.headPhoto;
          this.uploadedCoverPath = response.data.coverPhoto;
          this.previousUploadedHeadPath = response.data.headPhoto;
          this.previousUploadedCoverPath = response.data.coverPhoto;
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.show("حدث خطأ برجاء المحاولة مره اخري ❌");
        },
      });
    }
  }

  private setupActiveMenuItems(): void {
    if (
      this.router.url.includes("new-event") ||
      this.router.url.includes("edit-event")
    ) {
      const element = document.getElementById("الفعاليات");
      if (element) {
        element.classList.add("active");
      }
      if (this.router.url.includes("edit-event")) {
        this.editMode = true;
        if (this.url.snapshot.params["id"]) {
          const id = this.url.snapshot.params["id"];
          this.eventId = id;
          this.loadEventData(this.eventId);
        }
      } else {
        this.editMode = false;
        this.eventId = this.generateGuid();
      }
    } else {
      const itemsToDeactivate = [
        "الرئيسية",
        "المشرفين",
        "المستخدمين",
        "طلبات المشرفين",
        "فئات المناسبات",
        "العروض",
        "المسئولين",
      ];

      itemsToDeactivate.forEach((item) => {
        const element = document.getElementById(item);
        if (element) {
          element.classList.remove("active");
        }
      });
    }
  }

  private initializeContentHeader(): void {
    this.contentHeader = {
      headerTitle: this.editMode ? "تعديل فعالية" : "إنشاء فعالية جديد",
      actionButton: false,
    };
  }

  generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  onFileSelected(event: Event, type: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (type === "eventImage") {
        this.selectedHeadPhoto = file;
      } else if (type === "eventCover") {
        this.selectedCoverPhoto = file;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (type === "eventImage") {
          this.previewUrl1 = reader.result;
        } else if (type === "eventCover") {
          this.previewUrl2 = reader.result;
        }
      };
      reader.readAsDataURL(file);

      // Auto request upload
      const entityType = 2; // Event
      const usageType = type === "eventCover" ? 2 : 1; // Cover = 2, Head = 1

      this.mediaService.uploadMedia(file, this.eventId!, entityType, usageType).subscribe({
        next: (response) => {
          console.log("Uploaded successfully:", response.filePath);
          if (type === "eventCover") {
            // Delete previous temporary cover photo if it was uploaded in this session
            if (this.uploadedCoverPath && this.uploadedCoverPath !== this.previousUploadedCoverPath) {
              this.mediaService.deleteMedia(this.uploadedCoverPath).subscribe();
            }
            this.uploadedCoverPath = response.filePath;
          } else {
            // Delete previous temporary head photo if it was uploaded in this session
            if (this.uploadedHeadPath && this.uploadedHeadPath !== this.previousUploadedHeadPath) {
              this.mediaService.deleteMedia(this.uploadedHeadPath).subscribe();
            }
            this.uploadedHeadPath = response.filePath;
          }
          this.toastService.show("تم تحميل الصورة بنجاح ✅");
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.show("خطأ في تحميل الصورة ❌");
        }
      });
    }
  }

  save(): void {
    if (this.eventForm.invalid) {
      this.markFormGroupTouched(this.eventForm);
      this.toastService.show("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    if (!this.uploadedCoverPath || !this.uploadedHeadPath) {
      this.toastService.show("يرجى تحميل صور الغلاف والرأس");
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";
    this.successMessage = "";
    this.eventForm.value.eventSchedules.forEach((element) => {
      const startTime = new Date(element.startTime);
      startTime.setHours(startTime.getHours() - 1);
      element.startTime = startTime;
      const endTime = new Date(element.endTime);
      endTime.setHours(endTime.getHours() - 1);
      element.endTime = endTime;
    });
    var mappedValue = this.mapEventForm(this.eventForm.value);
    
    const role = localStorage.getItem("b2b_role");
    const tokenDataStr = localStorage.getItem("b2b_user_data");
    let organizerId = "";
    if (tokenDataStr) {
      try {
        const tokenData = JSON.parse(tokenDataStr);
        organizerId = tokenData.userId || tokenData.id || tokenData.nameid || tokenData["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] || "";
      } catch (e) {
        console.error("Failed to parse token data:", e);
      }
    }

    this.api.addEvent(mappedValue).subscribe({
      next: (response) => {
        const eventId = response.data.id;
        
        // Link the uploaded photos in Core API
        const imagePayload = {
          eventId: eventId,
          coverPhoto: this.uploadedCoverPath || "",
          headPhoto: this.uploadedHeadPath || ""
        };
        
        this.api.addEventImages(imagePayload).subscribe({
          next: () => {
            // Clean up any extra photos in directories
            if (this.uploadedCoverPath) this.mediaService.keepMedia(this.uploadedCoverPath).subscribe();
            if (this.uploadedHeadPath) this.mediaService.keepMedia(this.uploadedHeadPath).subscribe();
            
            if (role === 'Organizer' && organizerId) {
              const assignPayload = {
                organizerId: organizerId,
                eventId: eventId
              };
              this.api.assignOrganizerToEvent(assignPayload).subscribe({
                next: () => {
                  this.isSaved = true;
                  this.isLoading = false;
                  this.successMessage = "Event created and assigned successfully!";
                  this.toastService.show("تمت الاضافة والتعيين بنجاح ✅");
                  setTimeout(() => {
                    window.location.href = "/events";
                  }, 2000);
                },
                error: (assignError: HttpErrorResponse) => {
                  if (assignError.status === 409) {
                    this.isSaved = true;
                    this.isLoading = false;
                    this.successMessage = "Event created and assigned successfully!";
                    this.toastService.show("تمت الإضافة والتعيين بنجاح ✅");
                    setTimeout(() => {
                      window.location.href = "/events";
                    }, 2000);
                    return;
                  }

                  this.isLoading = false;
                  this.errorMessage = "Failed to assign organizer to event: " + assignError.message;
                  this.toastService.show("تمت الإضافة ولكن فشل التعيين ❌");
                  setTimeout(() => {
                    window.location.href = "/events";
                  }, 2000);
                }
              });
            } else {
              this.isSaved = true;
              this.isLoading = false;
              this.successMessage = "Event and images created successfully!";
              this.toastService.show("تمت الاضافة بنجاح ✅");
              setTimeout(() => {
                window.location.href = "/events";
              }, 2000);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.isLoading = false;
            this.errorMessage = "Failed to save event image paths: " + error.message;
            this.toastService.show("خطأ في حفظ مسارات الصور ❌");
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.errorMessage = "Failed to create event: " + error.message;
        if (error.error && error.error.errors) {
          if (error.error.errors.Description) this.toastService.show(error.error.errors.Description[0]);
          if (error.error.errors.Policy) this.toastService.show(error.error.errors.Policy[0]);
          if (error.error.errors["EventSchedules[0].StartTime"]) {
            this.toastService.show("تاريخ انشاء الحدث قديم برجاء أختيار تاريخ قادم");
          }
        } else {
          this.toastService.show("حدث خطأ أثناء الإنشاء برجاء المحاولة مره اخري ❌");
        }
      },
    });
  }

  private mapEventForm(formValue: any) {
    console.log(formValue.eventSchedules);
    return {
      ...formValue,
      id: this.eventId, // Pre-generated ID
      eventSchedules: formValue.eventSchedules.map((schedule: any) => ({
        ...schedule,
        hallRowSeatDistribution: Array.isArray(schedule.hallRowSeatDistribution)
          ? schedule.hallRowSeatDistribution.map((row: any) =>
              Number(row.seats)
            )
          : null,
      })),
    };
  }

  update() {
    if (this.eventForm.invalid) {
      console.log('--- FORM VALIDATION ERRORS ---');
      Object.keys(this.eventForm.controls).forEach(key => {
        const errors = this.eventForm.get(key)?.errors;
        if (errors) {
          console.log(`Field [${key}] is invalid:`, errors);
        }
      });
      const schedules = this.eventForm.get('eventSchedules') as FormArray;
      schedules.controls.forEach((schedule, sIndex) => {
        if (schedule.invalid) {
          console.log(`Schedule [${sIndex}] is invalid:`, schedule.errors);
          if (schedule instanceof FormGroup) {
            Object.keys(schedule.controls).forEach(key => {
              const errors = schedule.get(key)?.errors;
              if (errors) {
                console.log(`  Schedule [${sIndex}] -> Field [${key}] is invalid:`, errors);
              }
            });
            const tickets = schedule.get('eventTicketsTypes') as FormArray;
            tickets.controls.forEach((ticket, tIndex) => {
              if (ticket.invalid) {
                console.log(`  Schedule [${sIndex}] -> Ticket [${tIndex}] is invalid:`, ticket.errors);
                if (ticket instanceof FormGroup) {
                  Object.keys(ticket.controls).forEach(key => {
                    const errors = ticket.get(key)?.errors;
                    if (errors) {
                      console.log(`    Schedule [${sIndex}] -> Ticket [${tIndex}] -> Field [${key}] is invalid:`, errors);
                    }
                  });
                }
              }
            });
          }
        }
      });
      this.markFormGroupTouched(this.eventForm);
      this.toastService.show("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";
    this.successMessage = "";

    // Get all schedule dates to find the earliest start and latest end
    const allSchedules = this.eventForm.value.eventSchedules;
    const allStartTimes = allSchedules.map(
      (schedule) => new Date(schedule.startTime)
    );
    const allEndTimes = allSchedules.map(
      (schedule) => new Date(schedule.endTime)
    );

    // Find the earliest start time and latest end time
    const overallStartTime = new Date(Math.min(...allStartTimes));
    const overallEndTime = new Date(Math.max(...allEndTimes));

    // Format the dates to ISO string
    const formattedStartTime = overallStartTime.toISOString();
    const formattedEndTime = overallEndTime.toISOString();
    this.eventForm.value.eventSchedules.forEach((element) => {
      if (!element.isCenematic) {
        element.hallRowSeatDistribution = null;
      }
      let startTime = new Date(element.startTime);
      startTime.setHours(startTime.getHours() - 1);
      element.startTime = startTime;
      let endTime = new Date(element.endTime);
      endTime.setHours(endTime.getHours() - 1);
      element.endTime = endTime;
    });
    const payload = {
      id: this.eventId,
      isActive: this.eventForm.value.isActive,
      nameAr: this.eventForm.value.nameAr,
      nameEn: this.eventForm.value.nameEn,
      description: this.eventForm.value.description,
      policy: this.eventForm.value.policy,
      country: this.eventForm.value.country,
      city: this.eventForm.value.city,
      address: this.eventForm.value.address,
      categoryId: this.eventForm.value.categoryId,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      longitude: this.eventForm.value.longitude,
      latitude: this.eventForm.value.latitude,
      eventSchedules: this.eventForm.value.eventSchedules.map((schedule) => ({
        id: schedule.id,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        eventId: this.eventId,
        hallRowSeatDistribution:
          schedule.hallRowSeatDistribution != null
            ? schedule.hallRowSeatDistribution.map((h) => h.seats)
            : null,
        eventTicketsTypes: schedule.eventTicketsTypes.map((ticket) => ({
          id: ticket.id,
          name: ticket.name,
          note: ticket.note,
          eventId: this.eventId,
          price: ticket.price,
          availableTickets: ticket.availableTickets,
          maximumAllowedPurchasedTicketsPerCustomer:
            ticket.maximumAllowedPurchasedTicketsPerCustomer,
        })),
      })),
    };
    this.api.editEvent(payload).subscribe({
      next: (response) => {
        if (response.success == false) {
          this.isLoading = false;
          this.toastService.show(response.message);
          return;
        }

        // Save image paths in Core API
        if (this.uploadedCoverPath || this.uploadedHeadPath) {
          const imagePayload = {
            eventId: this.eventId,
            coverPhoto: this.uploadedCoverPath || response.data.coverPhoto || "",
            headPhoto: this.uploadedHeadPath || response.data.headPhoto || ""
          };
          this.api.addEventImages(imagePayload).subscribe({
            next: () => {
              if (this.uploadedCoverPath) this.mediaService.keepMedia(this.uploadedCoverPath).subscribe();
              if (this.uploadedHeadPath) this.mediaService.keepMedia(this.uploadedHeadPath).subscribe();
              
              this.isSaved = true;
              this.isLoading = false;
              this.successMessage = "Event updated successfully!";
              this.toastService.show("تم التحديث بنجاح ✅");
              setTimeout(() => {
                window.location.href = "/events";
              }, 2000);
            },
            error: (error: HttpErrorResponse) => {
              this.isLoading = false;
              this.toastService.show("خطأ في حفظ مسارات الصور ❌");
            }
          });
        } else {
          this.isSaved = true;
          this.isLoading = false;
          this.successMessage = "Event updated successfully!";
          this.toastService.show("تم التحديث بنجاح ✅");
          setTimeout(() => {
            window.location.href = "/events";
          }, 2000);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.toastService.show(
          "حدث خطأ أثناء التحديث برجاء المحاولة مره اخري ❌"
        );
      },
    });
  }

  ngOnDestroy(): void {
    if (!this.isSaved) {
      // Clean up any uploaded media that wasn't saved!
      if (this.uploadedCoverPath && this.uploadedCoverPath !== this.previousUploadedCoverPath) {
        this.mediaService.deleteMedia(this.uploadedCoverPath).subscribe();
      }
      if (this.uploadedHeadPath && this.uploadedHeadPath !== this.previousUploadedHeadPath) {
        this.mediaService.deleteMedia(this.uploadedHeadPath).subscribe();
      }
    }
  }

  private uploadImages(eventId: string): void {
    // Left empty since upload is now handled automatically on selection
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // validation functions
  arabicTextValidator(control: AbstractControl) {
    if (!control.value) return null; // skip validation if empty

    const arabicRegex =
      /^[\u0600-\u06FF\s\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF0-9٠-٩\-\&\/\(\)\.,]+$/;
    return arabicRegex.test(control.value) ? null : { arabicOnly: true };
  }
  englishTextValidator(control: AbstractControl) {
    if (!control.value) return null;
    const englishRegex = /^[A-Za-z0-9\s\-\&\/\(\)\.,]+$/;
    return englishRegex.test(control.value) ? null : { englishOnly: true };
  }
  numbersOnlyValidator(control: AbstractControl) {
    if (!control.value) return null;
    const numbersRegex = /^[0-9]+$/;
    return numbersRegex.test(control.value) ? null : { numbersOnly: true };
  }
  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const start = group.get("startTime")?.value;
      const end = group.get("endTime")?.value;

      if (!start || !end) {
        return null; // don't validate if either field is empty
      }

      // Compare dates (ISO strings can be compared directly)
      return end >= start ? null : { invalidDateRange: true };
    };
  }
  private setupDateValidation(): void {
    this.eventSchedules.controls.forEach((schedule) => {
      schedule.get("startTime")?.valueChanges.subscribe(() => {
        schedule.get("endTime")?.updateValueAndValidity();
      });
      schedule.get("endTime")?.valueChanges.subscribe(() => {
        schedule.get("startTime")?.updateValueAndValidity();
      });
    });
  }
  uniqueTicketNameValidator(scheduleIndex: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null; // skip if empty
      const ticketTypesArray = this.getTicketTypes(scheduleIndex);
      const currentName = control.value;
      let duplicateCount = 0;
      ticketTypesArray.controls.forEach((ticketGroup, index) => {
        if (ticketGroup.get("name")?.value === currentName) {
          duplicateCount++;
        }
      });
      // If duplicateCount > 1, it means there's another ticket with same name
      return duplicateCount > 1 ? { duplicateTicketName: true } : null;
    };
  }

  private maximumOfTicketRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const availableTickets = group.get("availableTickets")?.value;
      const maximumAllowedPurchasedTicketsPerCustomer = group.get(
        "maximumAllowedPurchasedTicketsPerCustomer"
      )?.value;

      if (!availableTickets || !maximumAllowedPurchasedTicketsPerCustomer) {
        return null; // don't validate if either field is empty
      }

      // Compare dates (ISO strings can be compared directly)
      return availableTickets >= maximumAllowedPurchasedTicketsPerCustomer
        ? null
        : { invalidTicketRange: true };
    };
  }
  startTimeAfterNowValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const startTimeControl = formGroup.get("startTime");

      if (!startTimeControl || !startTimeControl.value) {
        return null;
      }

      const selectedStartTime = new Date(startTimeControl.value);
      const currentTime = new Date();

      // If selected time is in the past
      if (selectedStartTime <= currentTime) {
        return { startTimeInPast: true };
      }

      // Optional: You might want to add a buffer (e.g., at least 1 hour from now)
      const minimumBufferMinutes = 60; // 1 hour
      const minAllowedTime = new Date(
        currentTime.getTime() + minimumBufferMinutes * 60000
      );

      if (selectedStartTime <= minAllowedTime) {
        return { startTimeTooSoon: true };
      }

      return null;
    };
  }
  cinematicTicketTypeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isCinematic = control.get("isCenematic")?.value;
      const ticketTypes = control.get("eventTicketsTypes");

      if (!ticketTypes) return null;

      if (isCinematic && ticketTypes.value.length !== 1) {
        return { cinematicTicketTypeCount: true };
      }

      return null;
    };
  }
}
